import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useLocation } from 'react-router-dom';

const StyledAppBar = styled(AppBar)(({ scrolled }) => ({
  background: scrolled
    ? 'rgba(10, 22, 40, 0.95)'
    : 'transparent',
  backdropFilter: scrolled ? 'blur(10px)' : 'none',
  boxShadow: scrolled ? '0 4px 30px rgba(0, 0, 0, 0.3)' : 'none',
  transition: 'all 0.3s ease',
  borderBottom: scrolled ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
}));

const Logo = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.5rem',
  background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  letterSpacing: '-0.02em',
});

const NavButton = styled(Button)(({ active }) => ({
  color: active ? '#60a5fa' : '#94a3b8',
  fontSize: '0.95rem',
  fontWeight: 500,
  textTransform: 'none',
  padding: '8px 16px',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
}));

const LoginButton = styled(Button)({
  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
  color: '#fff',
  padding: '10px 24px',
  fontSize: '0.95rem',
  fontWeight: 600,
  borderRadius: '10px',
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
  },
});

const MobileDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    background: 'linear-gradient(180deg, #0a1628 0%, #1a365d 100%)',
    width: '100%',
    maxWidth: '300px',
  },
});

const navItems = [
  { label: 'Docs', path: '/docs' },
];

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <StyledAppBar position="fixed" scrolled={scrolled ? 1 : 0}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                mr: 4,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1.5,
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                  V
                </Typography>
              </Box>
              <Logo>Voxxil</Logo>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 1 }}>
              {navItems.map((item) => (
                <NavButton
                  key={item.path}
                  component={Link}
                  to={item.path}
                  active={isActive(item.path) ? 1 : 0}
                >
                  {item.label}
                </NavButton>
              ))}
            </Box>

            {/* Desktop Login Button */}
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <LoginButton component={Link} to="/login">
                Sign In
              </LoginButton>
            </Box>

            {/* Mobile Menu Button */}
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end' }}>
              <IconButton
                size="large"
                onClick={handleDrawerToggle}
                sx={{ color: '#fff' }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </StyledAppBar>

      {/* Mobile Drawer */}
      <MobileDrawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Logo>Voxxil</Logo>
            <IconButton onClick={handleDrawerToggle} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <List>
            {navItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  onClick={handleDrawerToggle}
                  sx={{
                    borderRadius: '8px',
                    mb: 1,
                    backgroundColor: isActive(item.path)
                      ? 'rgba(59, 130, 246, 0.1)'
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: isActive(item.path) ? '#60a5fa' : '#94a3b8',
                        fontWeight: 500,
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Box sx={{ mt: 4 }}>
            <LoginButton
              component={Link}
              to="/login"
              fullWidth
              onClick={handleDrawerToggle}
            >
              Sign In
            </LoginButton>
          </Box>
        </Box>
      </MobileDrawer>

    </>
  );
}

export default Header;
